import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { projects as projectsApi, financialAnalysis as faApi } from "../services/api"; // v3.4.571 faApi — بيانات العقد
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  FiArrowRight, FiEdit2, FiUpload, FiDownload, FiLink,
  FiFileText, FiCheck
} from "react-icons/fi";

const dLocal = (v) => { if (!v) return null; const s = String(v); if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d = new Date(s); return isNaN(d) ? s.slice(0, 10) : d.toLocaleDateString("en-CA"); }; // v3.4.571 — DATE بلا انزياح يوم
const fmt = (n) => n ? Number(n).toLocaleString("en-US") : "—";

const STATUS_CONFIG = {
  available: { label: "متاح", bg: "#dcfce7", color: "#166534" },
  held: { label: "محجوز مؤقت", bg: "#fef9c3", color: "#854d0e" },
  reserved: { label: "محجوز", bg: "#dbeafe", color: "#1e40af" },
  sold: { label: "مُباع", bg: "#fee2e2", color: "#991b1b" },
  available_sakani: { label: "متاح سكني فقط", bg: "#dcfce7", color: "#166534" }, // v3.4.587
  reserved_sakani: { label: "محجوز سكني", bg: "#dbeafe", color: "#1e40af" },
};

export default function UnitFullPage() {
  const { projectId, unitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const p = user?.permissions || {};
  const isAdmin = user?.role === "admin" && !user?.impersonating;
  // v3.3.319: احترام صلاحية تعديل بيانات الوحدة (admin أو مفعّلة في البروفايل)
  const projPerms = user?.module_perms?.projects || {};
  const canManage = isAdmin || projPerms.can_edit_unit;
  // v3.3.320: عرض الرسوم الإدارية + توزيع الدلالة (افتراضي TRUE)
  const showAdminFees = isAdmin || projPerms.can_view_admin_fees !== false;
  const showMarketerDalala = isAdmin || projPerms.can_view_marketer_dalala !== false;
  // v3.3.323: عرض الدراسة المالية (افتراضي TRUE)
  const showFinancialStudy = isAdmin || projPerms.can_view_financial_study !== false;
  // v3.3.351: عرض قسم دفعات السداد (افتراضي TRUE)
  const showUnitPayments = isAdmin || projPerms.can_view_unit_payments !== false;
  // v3.3.357: إدارة دفعات الوحدة (إضافة/حذف)
  const canAddPayments = isAdmin || projPerms.can_add_unit_payments;
  const canDeletePayments = isAdmin || projPerms.can_delete_unit_payments;
  // v3.3.358: تعديل الرسوم الإدارية ورسوم الشركة
  const canEditAdminFees = isAdmin || projPerms.can_edit_admin_fees;
  // v3.4.571 — بيانات العقد (تاريخ التوقيع / الشرط الجزائي / مدة العقد): نفس صلاحية تعديل تاريخ التوقيع
  const canEditContractTerms = isAdmin || !!user?.can_edit_info || !!user?.projects_can_edit || !!user?.collections_can_edit_contract_date;
  const [termsEdit, setTermsEdit] = useState(null);
  const [savingTerms, setSavingTerms] = useState(false);

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [unit, setUnit] = useState(null);
  const [priceHist, setPriceHist] = useState([]); // v3.4.843 — سجل أسعار الوحدة (قراءة)
  useEffect(() => { if (!unit?.id) return; projectsApi.unitPrices(unit.id).then((r) => setPriceHist(r.data?.history || [])).catch(() => {}); }, [unit?.id, unit?.price]);
  const [linkedReservation, setLinkedReservation] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({ to_reservation_id: "", transfer_type: "تنازل", notes: "" });
  const [expandedTransfer, setExpandedTransfer] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // v3.3.360 — state لتعديل اتفاق الدفعات + تعديل الرسوم
  const [editingAgreement, setEditingAgreement] = useState(false);
  const [agreement, setAgreement] = useState("");
  const [feesEditable, setFeesEditable] = useState(false);
  const [feesForm, setFeesForm] = useState({ admin_fees: "", company_fees: "" });
  const [savingFees, setSavingFees] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddScheduled, setShowAddScheduled] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: "", payment_date: new Date().toISOString().slice(0,10), notes: "" });
  const [newScheduled, setNewScheduled] = useState({ amount: "", due_date: "", notes: "" });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const pRes = await projectsApi.get(projectId);
      setProject(pRes.data.project);
      
      const foundUnit = pRes.data.units?.find(u => u.id === parseInt(unitId));
      if (foundUnit) {
        setUnit(foundUnit);
        setForm({ ...foundUnit });
        // v3.3.360 — تعبئة state للأقسام القابلة للتعديل
        setAgreement(foundUnit.payment_agreement || "");
        setFeesForm({
          admin_fees: foundUnit.admin_fees ?? "",
          company_fees: foundUnit.company_fees ?? "",
        });
        
        try {
          const paymentsRes = await projectsApi.getUnitPayments(unitId);
          setPayments(paymentsRes.data.payments || []);
        } catch { setPayments([]); }
        
        try {
          const spRes = await projectsApi.getUnitScheduledPayments(unitId);
          setScheduledPayments(spRes.data.scheduled_payments || []);
        } catch { setScheduledPayments([]); }
        
        if (foundUnit.status !== "available") {
          try {
            const rRes = await projectsApi.getLinkedReservation(unitId);
            setLinkedReservation(rRes.data.reservation || null);
          } catch { setLinkedReservation(null); }
        }
        
        try {
          const trRes = await projectsApi.getUnitTransfers(unitId);
          setTransfers(trRes.data.transfers || []);
        } catch { setTransfers([]); }
      }
    } catch (e) {
      toast.error("فشل تحميل البيانات");
    }
    setLoading(false);
  }, [projectId, unitId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await projectsApi.updateUnit(unit.id, form);
      toast.success("✅ تم الحفظ");
      setEditing(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    finally { setSaving(false); }
  };

  // v3.3.360 — Handlers موحّدة مع الـ popup
  const saveAgreement = async () => {
    try {
      await projectsApi.updatePaymentAgreement(unit.id, agreement);
      setEditingAgreement(false);
      setUnit(u => ({ ...u, payment_agreement: agreement }));
      toast.success("تم حفظ اتفاق الدفعات");
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
  };

  const saveFees = async () => {
    setSavingFees(true);
    try {
      await projectsApi.updateUnit(unit.id, {
        admin_fees: feesForm.admin_fees === "" ? null : Number(feesForm.admin_fees),
        company_fees: feesForm.company_fees === "" ? null : Number(feesForm.company_fees),
      });
      setUnit(u => ({ ...u, admin_fees: feesForm.admin_fees, company_fees: feesForm.company_fees }));
      setFeesEditable(false);
      toast.success("تم حفظ الرسوم");
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    finally { setSavingFees(false); }
  };

  const addPayment = async () => {
    if (!newPayment.amount || !newPayment.payment_date) return toast.error("المبلغ والتاريخ مطلوبان");
    try {
      await projectsApi.addUnitPayment(unit.id, newPayment);
      toast.success("تمت إضافة الدفعة");
      setNewPayment({ amount: "", payment_date: new Date().toISOString().slice(0,10), notes: "" });
      setShowAddPayment(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const deletePayment = async (id) => {
    if (!window.confirm("حذف هذه الدفعة؟")) return;
    try {
      await projectsApi.deleteUnitPayment(unit.id, id);
      toast.success("تم الحذف");
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const addScheduled = async () => {
    if (!newScheduled.amount || !newScheduled.due_date) return toast.error("المبلغ وتاريخ الاستحقاق مطلوبان");
    try {
      await projectsApi.addUnitScheduledPayment(unit.id, newScheduled);
      toast.success("تمت الإضافة");
      setNewScheduled({ amount: "", due_date: "", notes: "" });
      setShowAddScheduled(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const deleteScheduled = async (id) => {
    if (!window.confirm("حذف هذه الدفعة المجدولة؟")) return;
    try {
      await projectsApi.deleteUnitScheduledPayment(unit.id, id);
      toast.success("تم الحذف");
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  // v3.3.458 — Mark scheduled payment as paid (also creates a real payment row)
  const markScheduledPaid = async (sp) => {
    if (!window.confirm(`تأكيد سداد دفعة ${fmt(sp.amount)} ر.س؟\nسيتم إضافتها للدفعات المسددة وتحديث المُحصّل.`)) return;
    try {
      await projectsApi.markScheduledPaymentPaid(unit.id, sp.id);
      toast.success("تم تسجيل السداد · المُحصّل محدَّث");
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  // v3.3.458 — Reverse mark-paid (deletes the linked payment)
  const unmarkScheduledPaid = async (sp) => {
    if (!window.confirm("إلغاء سداد هذه الدفعة؟\nسيتم حذف الدفعة من السجل.")) return;
    try {
      await projectsApi.unmarkScheduledPaymentPaid(unit.id, sp.id);
      toast.success("تم إلغاء السداد");
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  // v3.3.459 — Receipt upload for a payment
  const uploadReceiptForPayment = async (paymentId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      toast.loading("جاري رفع السند...", { id: "upl" });
      await projectsApi.uploadPaymentReceipt(unit.id, paymentId, formData);
      toast.success("تم رفع السند", { id: "upl" });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل رفع السند", { id: "upl" });
    }
  };
  const removeReceiptFromPayment = async (paymentId) => {
    if (!window.confirm("إزالة ربط السند بالدفعة؟ (لن يُحذف الملف نفسه)")) return;
    try {
      await projectsApi.removePaymentReceipt(unit.id, paymentId);
      toast.success("تم");
      loadData();
    } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const handleOpenReservation = (resId) => {
    navigate("/", { state: { openReservationId: resId } });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8fafc" }}>
        <div style={{ textAlign: "center", color: "#64748b" }}>⏳ جاري التحميل...</div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8fafc" }}>
        <div style={{ textAlign: "center", color: "#64748b" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏠</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>الوحدة غير موجودة</div>
          <Link to="/projects" style={{ color: "#2563eb", marginTop: 16, display: "inline-block" }}>العودة للمشاريع</Link>
        </div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[unit.status] || STATUS_CONFIG.available;
  const docs = unit.documents?.filter(Boolean) || [];
  const r = linkedReservation;

  const Field = ({ label, value, highlight, money }) => (
    <div>
      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: highlight ? 700 : 500,
        color: highlight && value ? "#166534" : value ? "#1e293b" : "#cbd5e1" }}>
        {money && value ? `${fmt(value)} ر.س` : (value || "—")}
      </div>
    </div>
  );

  const FRow = ({ label, value, highlight, isTotal, computed }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: isTotal ? "10px 12px" : "8px 12px", marginBottom: 4,
      background: isTotal ? "#f0fdf4" : highlight ? "#fefce8" : computed ? "#eff6ff" : "#f8fafc",
      border: `1px solid ${isTotal ? "#86efac" : highlight ? "#fef08a" : computed ? "#bfdbfe" : "#f1f5f9"}`,
      borderRadius: 8 }}>
      <div style={{ fontSize: isTotal ? 13 : 12, color: isTotal ? "#166534" : highlight ? "#92400e" : computed ? "#1d4ed8" : "#64748b", fontWeight: isTotal ? 700 : 600 }}>{label}</div>
      <div style={{ fontSize: isTotal ? 15 : 13, fontWeight: isTotal ? 800 : 600, color: value ? (isTotal ? "#166534" : highlight ? "#92400e" : computed ? "#1d4ed8" : "#1e293b") : "#cbd5e1" }}>{value || "—"}</div>
    </div>
  );

  const SectionHeader = ({ icon, title, bg, borderColor, textColor, extra }) => (
    <div style={{ padding: "10px 16px", background: bg || "#f8fafc", borderBottom: `1px solid ${borderColor || "#e2e8f0"}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: textColor || "#1e293b" }}>{title}</span>
      </div>
      {extra}
    </div>
  );

  const priceOfficial = Number(unit.price) || 0;
  const priceSale = Number(r?.value) || 0;
  const discount = priceOfficial && priceSale ? priceOfficial - priceSale : 0;
  const commission = Number(r?.dalala_amount) || 0;
  const adminFees = Number(r?.admin_fees_amount) || 0;
  const area = Number(unit.area_sqm || unit.area) || 0;
  const pricePerMeterOfficial = priceOfficial && area ? Math.round(priceOfficial / area) : 0;
  const pricePerMeterSale = priceSale && area ? Math.round(priceSale / area) : 0;
  const netSalePrice = priceSale - commission - adminFees;
  const totalPaid = payments.reduce((sum, pay) => sum + Number(pay.amount || 0), 0);
  const remaining = priceSale - totalPaid;
  const resaleNet = Number(unit.resale_net_price) || 0;
  const resaleComm = Number(unit.resale_marketer_commission) || 0;
  const resaleFees = Number(unit.resale_company_fees) || 0;
  const resaleTotal = resaleNet + resaleComm + resaleFees;

  return (
    <div dir="rtl" style={{ minHeight: "100vh", background: "#f1f5f9" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, maxWidth: 1600, margin: "0 auto" }}>
          <button onClick={() => navigate("/projects")} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}>
            <FiArrowRight size={18} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#64748b" }}>
            <Link to="/projects" style={{ color: "#64748b", textDecoration: "none" }}>المشاريع</Link>
            <span>/</span>
            <span>{project?.name}</span>
            <span>/</span>
            <span style={{ color: "#1e293b", fontWeight: 600 }}>{unit.unit_label || unit.unit_code}</span>
          </div>
          <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ background: sc.bg, color: sc.color, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{sc.label}</span>
            {/* زر فتح في الـ Popup للتعديل الكامل */}
            <button onClick={() => navigate(`/projects`, { state: { openProjectId: parseInt(projectId), openUnitId: parseInt(unitId) } })}
              style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: "#166534", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
              ✏️ تعديل كامل
            </button>
            {!editing && canManage && (
              <button onClick={() => setEditing(true)} style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: "#2563eb", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                <FiEdit2 size={14} /> تعديل
              </button>
            )}
            {canManage && unit.status !== "available" && (
              <button onClick={() => setShowTransferModal(true)} style={{ background: "#fefce8", border: "1px solid #fef08a", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: "#92400e", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                🔄 نقل / تنازل
              </button>
            )}
            {editing && (
              <>
                <button onClick={handleSave} disabled={saving} style={{ background: "#166534", border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  {saving ? "⏳" : <FiCheck size={14} />} حفظ
                </button>
                <button onClick={() => { setEditing(false); setForm({ ...unit }); }} style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 12px", cursor: "pointer", color: "#64748b", fontSize: 12, fontWeight: 600 }}>إلغاء</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: 24, maxWidth: 1600, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: unit.status === "available" ? "1fr" : "1fr 1fr 1fr", gap: 20, alignItems: "start", maxWidth: unit.status === "available" ? 500 : "none" }}>
          
          {/* Column 1 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="🏠" title="بيانات الوحدة" />
              <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="المبنى" value={unit.building_name} />
                <Field label="الدور #" value={unit.floor_number} />
                <Field label="رمز الشقة" value={unit.unit_label} />
                <Field label="الواجهة" value={unit.facade} />
                <Field label="عدد الغرف" value={unit.rooms} />
                <Field label="عدد الحمام" value={unit.bathrooms} />
                <Field label="المطبخ" value={unit.kitchen} />
                <Field label="الصالة" value={unit.living_room} />
                <Field label="رقم الوحد" value={unit.unit_number} />
                <Field label="المساحة" value={area ? `${area} م²` : null} />
                <Field label="نوع الوحدة" value={unit.unit_type} />
                <Field label="السعر الرسمي" value={unit.price} money />
                <Field label="الحالة" value={sc.label} />
              </div>
              {priceHist.length > 0 && ( /* v3.4.843 — سجل الأسعار: الساري ثم القديمة بتاريخها وملاحظتها */
                <div style={{ margin: "0 16px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>💰 سجل الأسعار</div>
                  {priceHist.map((h, i) => (
                    <div key={h.id} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12, color: i ? "#64748b" : "#1e293b" }}>
                      <span style={{ fontFamily: "monospace", fontWeight: i ? 500 : 800, textDecoration: i ? "line-through" : "none", color: i ? "#94a3b8" : "#2563eb" }}>{fmt(h.price)}</span>
                      {!i && <span style={{ fontSize: 10, fontWeight: 700, color: "#166534", background: "#dcfce7", borderRadius: 999, padding: "1px 7px" }}>الساري</span>}
                      <span style={{ fontSize: 10.5 }}>{String(h.created_at || "").slice(0, 10)}</span>
                      {h.note && <span style={{ fontSize: 10.5, color: "#475569" }}>({h.note})</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {unit.status !== "available" && (
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="🔗" title="الحجز" bg="#eff6ff" borderColor="#bfdbfe" textColor="#1e40af" />
              <div style={{ padding: 16 }}>
                {r ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, background: "#f0fdf4", padding: "10px 14px", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                      <span style={{ fontSize: 12, color: "#166534", fontWeight: 600 }}>رقم الحجز</span>
                      {/* v3.3.433 — Use serial_number for consistency */}
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#166534" }}>#{r.id}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div style={{ gridColumn: "1/-1" }}><Field label="العميل" value={r.client_name} highlight /></div>
                      <Field label="تاريخ الحجز" value={r.date?.slice(0, 10)} />
                      <Field label="نوع العملية" value={r.operation_type} />
                    </div>
                    <button onClick={() => handleOpenReservation(r.id)} style={{ width: "100%", padding: "10px", background: "#2563eb", border: "none", borderRadius: 8, cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600, marginTop: 12 }}>فتح الحجز</button>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>
                    <FiLink size={28} style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 13 }}>لا يوجد حجز مرتبط</div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* الحجوزات السابقة — من عمليات النقل المكتملة */}
            {transfers.filter(t => t.status === "completed").length > 0 && (
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="📜" title={`الحجوزات السابقة (${transfers.filter(t => t.status === "completed").length})`} bg="#f5f3ff" borderColor="#ede9fe" textColor="#7c3aed" />
              <div style={{ padding: 12 }}>
                {transfers.filter(t => t.status === "completed").map(t => (
                  <div key={t.id} onClick={() => handleOpenReservation(t.from_reservation_id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 6, cursor: "pointer", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "#f8fafc"}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>حجز #{t.from_serial || t.from_reservation_id}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{t.from_client}</div>
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700 }}>{t.transfer_type}</span>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>{t.executed_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="📄" title={`المستندات (${docs.length})`} extra={canManage && <button style={{ background: "#166534", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", color: "#fff", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><FiUpload size={12} /> رفع</button>} />
              <div style={{ padding: 12, maxHeight: 250, overflowY: "auto" }}>
                {docs.length > 0 ? docs.map((doc, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 6 }}>
                    <FiFileText style={{ color: "#64748b" }} />
                    <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{doc.file_name}</div><div style={{ fontSize: 10, color: "#94a3b8" }}>{doc.doc_type}{doc.uploaded_at ? ` — رُفع ${new Date(doc.uploaded_at).toLocaleDateString("en-GB", { timeZone: "Asia/Riyadh" })}` : ""}</div></div>
                    {doc.drive_web_link && <a href={doc.drive_web_link} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}><FiDownload /></a>}
                  </div>
                )) : <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 12 }}>لا توجد مستندات</div>}
              </div>
            </div>
          </div>

          {/* Column 2 - Only for reserved/sold */}
          {unit.status !== "available" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="💰" title="التفاصيل المالية" bg="#fefce8" borderColor="#fef08a" textColor="#854d0e" />
              <div style={{ padding: 16 }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 8 }}>💵 تفاصيل السعر</div>
                  <FRow label="السعر الرسمي" value={priceOfficial ? `${fmt(priceOfficial)} ر.س` : null} />
                  <FRow label="الخصم" value={discount !== 0 ? `${fmt(discount)} ر.س` : "(لا يوجد خصم)"} />
                  <FRow label="سعر البيع تنفيذ" value={priceSale ? `${fmt(priceSale)} ر.س` : null} highlight />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 8 }}>🏦 تفاصيل العربون</div>
                  <FRow label="العربون" value={r?.deposit_amount ? `${fmt(r.deposit_amount)} ر.س` : null} />
                  <FRow label="حالة العربون" value={r?.deposit_status} />
                  {r?.bank_account && <FRow label="البنك" value={r.bank_account} />}
                </div>
              </div>
            </div>

            {/* v3.3.351 — قسم دفعات السداد قابل للإخفاء بصلاحية */}
            {showUnitPayments && (
            <>
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="💳" title="دفعات السداد" extra={canAddPayments && (
                <button onClick={() => setShowAddPayment(!showAddPayment)}
                  style={{ background: showAddPayment ? "#dc2626" : "#166534", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", color: "#fff", fontSize: 11, fontWeight: 600 }}>
                  {showAddPayment ? "إلغاء" : "+ إضافة دفعة"}
                </button>
              )} />
              <div style={{ padding: 16 }}>
                {/* v3.4.571 — بيانات العقد */}
                {(() => {
                  const sd = dLocal(unit.contract_signed_date);
                  const dur = unit.contract_duration_months != null ? Number(unit.contract_duration_months) : null;
                  const end = sd && dur != null ? (() => { const d = new Date(sd + "T00:00:00Z"); d.setUTCMonth(d.getUTCMonth() + dur); return d.toISOString().slice(0, 10); })() : null;
                  const save = async () => {
                    setSavingTerms(true);
                    try {
                      const r = await faApi.setContractTerms(unit.id, termsEdit);
                      setUnit((u) => ({ ...u, contract_signed_date: r.data.contract_signed_date, penalty_amount: r.data.penalty_amount, contract_duration_months: r.data.contract_duration_months }));
                      setTermsEdit(null); toast.success("حُفظت بيانات العقد");
                    } catch (e) { toast.error(e.response?.data?.error || "تعذّر الحفظ"); }
                    setSavingTerms(false);
                  };
                  const inp = { width: "100%", boxSizing: "border-box", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", fontSize: 12.5, direction: "ltr" };
                  const cell = (label, value, color) => <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 6px", textAlign: "center" }}><div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{label}</div><div style={{ fontSize: 14, fontWeight: 800, color: value ? color : "#cbd5e1", marginTop: 2 }}>{value || "—"}</div></div>;
                  return (
                    <div style={{ marginBottom: 16, padding: 10, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>📄 بيانات العقد</span>
                        {canEditContractTerms && !termsEdit && <button onClick={() => setTermsEdit({ contract_signed_date: sd || "", penalty_amount: unit.penalty_amount ?? "", contract_duration_months: dur ?? "" })} style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 10.5, fontFamily: "inherit" }}>تعديل</button>}
                      </div>
                      {termsEdit ? (
                        <div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                            <label style={{ fontSize: 10, color: "#64748b" }}>تاريخ توقيع العقد<input type="date" value={termsEdit.contract_signed_date} onChange={(e) => setTermsEdit({ ...termsEdit, contract_signed_date: e.target.value })} style={inp} /></label>
                            <label style={{ fontSize: 10, color: "#64748b" }}>آخر مبلغ للشرط الجزائي (ر.س)<input type="number" min={0} value={termsEdit.penalty_amount} onChange={(e) => setTermsEdit({ ...termsEdit, penalty_amount: e.target.value })} style={inp} /></label>
                            <label style={{ fontSize: 10, color: "#64748b" }}>مدة العقد (أشهر)<input type="number" min={0} max={600} value={termsEdit.contract_duration_months} onChange={(e) => setTermsEdit({ ...termsEdit, contract_duration_months: e.target.value })} style={inp} /></label>
                          </div>
                          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                            <button onClick={save} disabled={savingTerms} style={{ background: "#166534", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: "inherit" }}>{savingTerms ? "…" : "حفظ"}</button>
                            <button onClick={() => setTermsEdit(null)} style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 11.5, fontFamily: "inherit" }}>إلغاء</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                            {cell("تاريخ توقيع العقد", sd, "#0f172a")}
                            {cell("آخر مبلغ للشرط الجزائي", unit.penalty_amount != null ? `${fmt(unit.penalty_amount)} ر.س` : null, "#b45309")}
                            {cell("مدة العقد", dur != null ? `${dur} شهر` : null, "#1d4ed8")}
                          </div>
                          {end && <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 6, textAlign: "center" }}>ينتهي العقد في <b style={{ color: "#0f172a", direction: "ltr", display: "inline-block" }}>{end}</b></div>}
                        </>
                      )}
                    </div>
                  );
                })()}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                  <div style={{ background: "#dcfce7", padding: 12, borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#166534", fontWeight: 600 }}>الدفعات المسددة</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#166534" }}>{fmt(totalPaid)} ر.س</div>
                  </div>
                  <div style={{ background: "#fee2e2", padding: 12, borderRadius: 8, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#991b1b", fontWeight: 600 }}>المتبقي</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#991b1b" }}>{fmt(remaining)} ر.س</div>
                  </div>
                </div>

                {/* v3.3.360 — اتفاق نظام الدفعات (موحّد مع popup) */}
                <div style={{ marginBottom: 16, padding: 10, background: "#fafafa", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>📝 اتفاق نظام الدفعات</span>
                    {!editingAgreement && canAddPayments && (
                      <button onClick={() => setEditingAgreement(true)} style={{ background:"#f1f5f9", color:"#64748b", border:"none", borderRadius:4, padding:"2px 8px", fontSize:10, cursor:"pointer" }}>تعديل</button>
                    )}
                  </div>
                  {editingAgreement ? (
                    <div>
                      <textarea value={agreement} onChange={e => setAgreement(e.target.value)} rows={3}
                        placeholder="مثال: دفعة أولى 10% عند التوقيع، 40% عند الاستلام..."
                        style={{ width:"100%", padding:"8px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:11, fontFamily:"inherit", resize:"vertical" }} />
                      <div style={{ display:"flex", gap:6, marginTop:6 }}>
                        <button onClick={saveAgreement} style={{ background:"#22c55e", color:"#fff", border:"none", borderRadius:5, padding:"4px 12px", fontSize:11, cursor:"pointer" }}>حفظ</button>
                        <button onClick={() => { setEditingAgreement(false); setAgreement(unit.payment_agreement || ""); }} style={{ background:"#e2e8f0", color:"#64748b", border:"none", borderRadius:5, padding:"4px 12px", fontSize:11, cursor:"pointer" }}>إلغاء</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ background:"#fff", borderRadius:6, padding:8, fontSize:11, color: agreement ? "#374151" : "#94a3b8", whiteSpace:"pre-wrap", border: "1px solid #e5e7eb" }}>
                      {agreement || "لم يتم تحديد اتفاق"}
                    </div>
                  )}
                </div>

                {/* v3.3.360 — نموذج إضافة دفعة (inline) */}
                {showAddPayment && canAddPayments && (
                  <div style={{ background:"#f0f9ff", padding:12, borderRadius:8, marginBottom:12, border:"1px solid #bfdbfe" }}>
                    <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                      <input type="number" placeholder="المبلغ" value={newPayment.amount}
                        onChange={e => setNewPayment({...newPayment, amount: e.target.value})}
                        style={{ flex:1, padding:"6px 10px", border:"1px solid #bfdbfe", borderRadius:6, fontSize:12, fontFamily:"inherit" }} />
                      <input type="date" value={newPayment.payment_date}
                        onChange={e => setNewPayment({...newPayment, payment_date: e.target.value})}
                        style={{ padding:"6px 10px", border:"1px solid #bfdbfe", borderRadius:6, fontSize:12, fontFamily:"inherit" }} />
                    </div>
                    <input type="text" placeholder="ملاحظات (اختياري)" value={newPayment.notes}
                      onChange={e => setNewPayment({...newPayment, notes: e.target.value})}
                      style={{ width:"100%", padding:"6px 10px", border:"1px solid #bfdbfe", borderRadius:6, fontSize:12, fontFamily:"inherit", marginBottom: 8 }} />
                    <button onClick={addPayment} style={{ background:"#2563eb", color:"#fff", border:"none", borderRadius:6, padding:"6px 14px", fontSize:11, cursor:"pointer", fontWeight:600 }}>
                      حفظ الدفعة
                    </button>
                  </div>
                )}

                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 8 }}>📋 الدفعات المسددة</div>
                {payments.length > 0 ? (
                  <div style={{ maxHeight: 250, overflowY: "auto" }}>
                    {payments.map((pay, i) => (
                      <div key={i} style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: 8, marginBottom: 6, border: "1px solid #e2e8f0" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "#64748b", fontSize: 12 }}>{pay.payment_date?.slice(0, 10)}</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 700, color: "#166534", fontSize: 14 }}>{fmt(pay.amount)} ر.س</span>
                            {/* v3.3.459 — Upload receipt button (only if no receipt yet) */}
                            {canAddPayments && !pay.receipt_drive_link && (
                              <label
                                title="إرفاق سند قبض/إيصال"
                                style={{ background: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc", borderRadius: 5, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                              >
                                📎 إرفاق سند
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) uploadReceiptForPayment(pay.id, f);
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                            )}
                            {/* v3.3.360 — زر حذف الدفعة (موحّد مع popup) */}
                            {canDeletePayments && (
                              <button onClick={() => deletePayment(pay.id)} title="حذف الدفعة"
                                style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", fontSize:16, padding:2 }}>×</button>
                            )}
                          </div>
                        </div>
                        {pay.notes && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{pay.notes}</div>}
                        {pay.receipt_drive_link && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                            <a href={pay.receipt_drive_link} target="_blank" rel="noopener noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#2563eb", textDecoration: "none", background: "#eff6ff", padding: "3px 8px", borderRadius: 6, border: "1px solid #bfdbfe" }}>
                              <FiDownload size={12} /> {pay.receipt_file_name || "سند القبض"}
                            </a>
                            {canAddPayments && (
                              <button
                                onClick={() => removeReceiptFromPayment(pay.id)}
                                title="إزالة ربط السند (يمكن رفع غيره)"
                                style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, padding: 0 }}
                              >×</button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 12 }}>لا توجد دفعات مسددة</div>}
              </div>
            </div>

            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="📅" title="جدول الدفعات المتفق عليها" extra={canAddPayments && (
                <button onClick={() => setShowAddScheduled(!showAddScheduled)}
                  style={{ background: showAddScheduled ? "#dc2626" : "#166534", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", color: "#fff", fontSize: 11, fontWeight: 600 }}>
                  {showAddScheduled ? "إلغاء" : "+ إضافة"}
                </button>
              )} />
              <div style={{ padding: 16 }}>
                {/* v3.3.360 — نموذج إضافة دفعة مجدولة (inline) */}
                {showAddScheduled && canAddPayments && (
                  <div style={{ background:"#fefce8", padding:10, borderRadius:6, marginBottom:10, display:"flex", gap:8, alignItems:"flex-end" }}>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:10, color:"#92400e", display:"block", marginBottom:3 }}>المبلغ</label>
                      <input type="number" value={newScheduled.amount}
                        onChange={e => setNewScheduled({...newScheduled, amount: e.target.value})}
                        style={{ width:"100%", padding:"5px 8px", border:"1px solid #fde68a", borderRadius:5, fontSize:11, fontFamily:"inherit" }} />
                    </div>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:10, color:"#92400e", display:"block", marginBottom:3 }}>تاريخ الاستحقاق</label>
                      <input type="date" value={newScheduled.due_date}
                        onChange={e => setNewScheduled({...newScheduled, due_date: e.target.value})}
                        style={{ width:"100%", padding:"5px 8px", border:"1px solid #fde68a", borderRadius:5, fontSize:11, fontFamily:"inherit" }} />
                    </div>
                    <button onClick={addScheduled} style={{ background:"#f59e0b", color:"#fff", border:"none", borderRadius:5, padding:"6px 12px", fontSize:11, cursor:"pointer", fontWeight:600 }}>
                      حفظ
                    </button>
                  </div>
                )}

                {scheduledPayments.length > 0 ? (
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {scheduledPayments.map((sp, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: sp.is_paid ? "#f0fdf4" : "#f8fafc", border: `1px solid ${sp.is_paid ? "#bbf7d0" : "#e2e8f0"}`, borderRadius: 8, marginBottom: 6 }}>
                        <div><div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{sp.label || `دفعة ${i + 1}`}</div><div style={{ fontSize: 10, color: "#64748b" }}>{sp.due_date?.slice(0, 10)}</div></div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ textAlign: "left" }}><div style={{ fontSize: 13, fontWeight: 700, color: sp.is_paid ? "#166534" : "#1e293b" }}>{fmt(sp.amount)} ر.س</div>{sp.is_paid && <span style={{ fontSize: 10, color: "#166534" }}>✓ مدفوعة</span>}</div>
                          {/* v3.3.458 — زر "تم السداد" للدفعات غير المدفوعة */}
                          {!sp.is_paid && canAddPayments && (
                            <button onClick={() => markScheduledPaid(sp)} title="تأكيد سداد هذه الدفعة وإضافتها للمحصّل"
                              style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 5, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                              ✓ تم السداد
                            </button>
                          )}
                          {/* v3.3.458 — زر "إلغاء السداد" للدفعات المدفوعة */}
                          {sp.is_paid && canAddPayments && (
                            <button onClick={() => unmarkScheduledPaid(sp)} title="إلغاء السداد وحذف الدفعة من المحصّل"
                              style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: 5, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                              ↶ إلغاء
                            </button>
                          )}
                          {/* v3.3.360 — زر حذف للدفعات المجدولة غير المدفوعة (موحّد مع popup) */}
                          {!sp.is_paid && canDeletePayments && (
                            <button onClick={() => deleteScheduled(sp.id)} title="حذف الدفعة المجدولة"
                              style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", fontSize:14, padding:2 }}>×</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 12 }}>لم يتم تحديد جدول دفعات</div>}
              </div>
            </div>
            </>
            )}
          </div>
          )}

          {/* Column 3 - Only for reserved/sold */}
          {unit.status !== "available" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {showFinancialStudy && (
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="📊" title="الدراسة المالية" bg="#f0fdf4" borderColor="#bbf7d0" textColor="#166534" />
              <div style={{ padding: 16 }}>
                {/* v3.3.753 — سعر المتر (تنفيذ) تحت سعر البيع تنفيذ + سعر المتر (صافي) تحت الصافي */}
                <FRow label="سعر البيع الرسمي" value={priceOfficial ? `${fmt(priceOfficial)} ر.س` : null} />
                <FRow label="سعر المتر الرسمي" value={pricePerMeterOfficial ? `${fmt(pricePerMeterOfficial)} ر.س` : null} computed />
                <FRow label="سعر البيع تنفيذ" value={priceSale ? `${fmt(priceSale)} ر.س` : null} highlight />
                <FRow label="سعر المتر (تنفيذ)" value={pricePerMeterSale ? `${fmt(pricePerMeterSale)} ر.س` : null} computed />
                <FRow label="العمولة" value={commission ? `${fmt(commission)} ر.س` : null} />
                {showAdminFees && <FRow label="رسوم إدارية" value={adminFees ? `${fmt(adminFees)} ر.س` : null} />}
                <FRow label="صافي سعر البيع" value={netSalePrice > 0 ? `${fmt(netSalePrice)} ر.س` : null} isTotal />
                <FRow label="سعر المتر (صافي)" value={(netSalePrice > 0 && area) ? `${fmt(Math.round(netSalePrice / area))} ر.س` : null} computed />
                {priceSale > 0 && showAdminFees && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 8 }}>صافي = سعر البيع ({fmt(priceSale)}) − العمولة ({fmt(commission)}) − رسوم ({fmt(adminFees)})</div>}
              </div>
            </div>
            )}

            {/* v3.3.360 — قسم الرسوم الإدارية القابل للتعديل (موحّد مع popup) */}
            {showAdminFees && (
              <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", background: "#eff6ff", borderBottom: "1px solid #bfdbfe", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1e40af" }}>💼 الرسوم الإدارية</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: "#64748b" }}>تُحسب ضمن الخصومات في صافي البيع</span>
                    {canEditAdminFees && (
                      <button onClick={() => setFeesEditable(prev => !prev)}
                        style={{ background: feesEditable ? "#dc2626" : "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                        title={feesEditable ? "قفل الحقول" : "فتح للتعديل"}>
                        {feesEditable ? "🔒 قفل" : "✏️ تعديل"}
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ padding: 14 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>رسوم إدارية (ر.س)</label>
                      <input type="number" value={feesForm.admin_fees}
                        onChange={e => setFeesForm({...feesForm, admin_fees: e.target.value})}
                        disabled={!feesEditable} placeholder="0"
                        style={{ width: "100%", padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, fontFamily: "inherit", background: feesEditable ? "#fff" : "#f8fafc", color: feesEditable ? "#1e293b" : "#475569" }} />
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>الافتراضي: 2,000 ر.س</div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>رسوم الشركة (ر.س)</label>
                      <input type="number" value={feesForm.company_fees}
                        onChange={e => setFeesForm({...feesForm, company_fees: e.target.value})}
                        disabled={!feesEditable} placeholder="0"
                        style={{ width: "100%", padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, fontFamily: "inherit", background: feesEditable ? "#fff" : "#f8fafc", color: feesEditable ? "#1e293b" : "#475569" }} />
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>الافتراضي: 3,000 ر.س</div>
                    </div>
                  </div>
                  {feesEditable && (
                    <div style={{ display: "flex", gap: 6, marginTop: 12, justifyContent: "flex-end" }}>
                      <button onClick={() => { setFeesEditable(false); setFeesForm({ admin_fees: unit.admin_fees ?? "", company_fees: unit.company_fees ?? "" }); }}
                        style={{ background:"#e2e8f0", color:"#64748b", border:"none", borderRadius:5, padding:"5px 12px", fontSize:11, cursor:"pointer", fontWeight:600 }}>إلغاء</button>
                      <button onClick={saveFees} disabled={savingFees}
                        style={{ background:"#22c55e", color:"#fff", border:"none", borderRadius:5, padding:"5px 14px", fontSize:11, cursor: savingFees ? "wait" : "pointer", fontWeight:600 }}>
                        {savingFees ? "جارٍ الحفظ..." : "💾 حفظ"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showMarketerDalala && (
            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              <SectionHeader icon="📈" title="المسوق والدلالة" />
              <div style={{ padding: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <Field label="المسوق" value={r?.marketer} highlight />
                  <Field label="نوع العملية" value={r?.operation_type} />
                  <Field label="إجمالي الدلالة" value={r?.dalala_amount} money highlight />
                  <Field label="حالة الدلالة" value={r?.commission_status_res} />
                </div>
                {r?.dalala_amount > 0 && (
                  <div style={{ background: "#f5f3ff", borderRadius: 8, padding: 12, border: "1px solid #ede9fe" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 10 }}>توزيع الدلالة</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <Field label="حصة الخارجي" value={r.ext_marketer_share} money />
                      <Field label="حصة الداخلي" value={r.int_marketer_share} money />
                      <Field label="الواحة" value={r.waha_share} money />
                      <Field label="م. المبيعات" value={r.sales_mgr_share} money />
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", border: resaleNet ? "1px solid #fde68a" : "none" }}>
              <SectionHeader icon="🔄" title="إعادة البيع" bg={resaleNet ? "#fffbeb" : "#f8fafc"} textColor={resaleNet ? "#92400e" : "#64748b"} 
                extra={canManage && (
                  <button onClick={() => navigate(`/projects`, { state: { openProjectId: parseInt(projectId), openUnitId: parseInt(unitId) } })}
                    style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "3px 10px", cursor: "pointer", color: "#64748b", fontSize: 11 }}>
                    {resaleNet ? "✏️ تعديل" : "+ إضافة"}
                  </button>
                )} />
              <div style={{ padding: 16 }}>
                {resaleNet ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                      <Field label="طريقة التصفية" value={unit.resale_filter_method} />
                      <Field label="صافي سعر إعادة البيع" value={resaleNet} money />
                      <Field label="عمولة مسوق" value={resaleComm} money />
                      <Field label="رسوم الشركة" value={resaleFees} money />
                    </div>
                    <div style={{ background: "#f0fdf4", padding: 12, borderRadius: 8, border: "1px solid #bbf7d0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>إجمالي سعر إعادة البيع</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#166534" }}>{fmt(resaleTotal)} ر.س</span>
                    </div>
                  </>
                ) : <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 12 }}>لا توجد بيانات إعادة بيع</div>}
              </div>
            </div>
          </div>
          )}

        </div>

        {/* ═══ طلبات نقل الوحدة ═══ */}
        {transfers.length > 0 && (
          <div style={{ marginTop: 20, background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
            <div style={{ padding: "10px 16px", background: "#fffbeb", borderBottom: "1px solid #fef08a", display: "flex", alignItems: "center", gap: 8 }}>
              <span>🔄</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>طلبات نقل الوحدة ({transfers.length})</span>
            </div>
            <div style={{ padding: 16 }}>
              {transfers.map((t) => {
                const statusMap = { pending_docs: { label: "بانتظار المستندات", bg: "#fef9c3", color: "#854d0e", icon: "⏳" }, ready: { label: "جاهز للتنفيذ", bg: "#dbeafe", color: "#1e40af", icon: "✅" }, completed: { label: "مكتمل", bg: "#dcfce7", color: "#166534", icon: "🎉" }, cancelled: { label: "ملغي", bg: "#fee2e2", color: "#991b1b", icon: "❌" } };
                const st = statusMap[t.status] || statusMap.pending_docs;
                const isPending = t.status === "pending_docs" || t.status === "ready";
                return (
                  <div key={t.id} style={{ padding: 16, background: isPending ? "#fffbeb" : "#f8fafc", border: `2px solid ${isPending ? "#fef08a" : "#e2e8f0"}`, borderRadius: 12, marginBottom: 12 }}>
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{st.icon}</span>
                        <span style={{ background: "#fefce8", color: "#92400e", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{t.transfer_type}</span>
                        <span style={{ background: st.bg, color: st.color, padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{st.label}</span>
                      </div>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>طلب #{t.id} — {t.created_at?.slice(0, 10)}</span>
                    </div>

                    {/* من → إلى */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 10, alignItems: "center", marginBottom: 4 }}>
                      <div onClick={() => t.from_reservation_id && handleOpenReservation(t.from_reservation_id)}
                        style={{ textAlign: "center", padding: 10, background: "#fee2e2", borderRadius: 10, cursor: t.from_reservation_id ? "pointer" : "default" }}
                        title="اضغط لفتح الحجز القديم في نافذة منفصلة">
                        <div style={{ fontSize: 10, color: "#991b1b", fontWeight: 600 }}>من حجز</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#991b1b" }}>#{t.from_serial || t.from_reservation_id}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{t.from_client || "—"}</div>
                        <div style={{ fontSize: 10, color: "#991b1b", marginTop: 4, textDecoration: "underline" }}>📂 فتح الحجز</div>
                      </div>
                      <span style={{ fontSize: 22, color: "#94a3b8" }}>←</span>
                      <div onClick={() => t.to_reservation_id && handleOpenReservation(t.to_reservation_id)}
                        style={{ textAlign: "center", padding: 10, background: t.to_reservation_id ? "#dcfce7" : "#f1f5f9", borderRadius: 10, border: !t.to_reservation_id ? "2px dashed #cbd5e1" : "none", cursor: t.to_reservation_id ? "pointer" : "default" }}>
                        <div style={{ fontSize: 10, color: t.to_reservation_id ? "#166534" : "#94a3b8", fontWeight: 600 }}>إلى حجز</div>
                        {t.to_reservation_id ? (
                          <>
                            <div style={{ fontSize: 15, fontWeight: 800, color: "#166534" }}>#{t.to_serial || t.to_reservation_id}</div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{t.to_client || "—"}</div>
                            <div style={{ fontSize: 10, color: "#166534", marginTop: 4, textDecoration: "underline" }}>📂 فتح الحجز</div>
                          </>
                        ) : <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>لم يُحدد بعد</div>}
                      </div>
                    </div>

                    {/* زر استعراض تفاصيل الحجز السابق */}
                    {t.from_reservation_id && (
                      <div style={{ marginBottom: 14 }}>
                        <button onClick={async () => {
                          if (expandedTransfer?.transferId === t.id) { setExpandedTransfer(null); return; }
                          setLoadingDetails(true);
                          try {
                            const res = await projectsApi.getReservationFullDetails(t.from_reservation_id);
                            setExpandedTransfer({ transferId: t.id, ...res.data });
                          } catch (e) { toast.error("فشل تحميل التفاصيل"); }
                          setLoadingDetails(false);
                        }} style={{ width: "100%", padding: "8px", background: expandedTransfer?.transferId === t.id ? "#fee2e2" : "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#991b1b", fontFamily: "inherit", marginTop: 6 }}>
                          {loadingDetails && expandedTransfer?.transferId !== t.id ? "⏳ جاري التحميل..." : expandedTransfer?.transferId === t.id ? "▲ إخفاء تفاصيل الحجز السابق" : "▼ استعراض تفاصيل الحجز السابق (السعر، الدفعات، السندات، المستندات)"}
                        </button>

                        {expandedTransfer?.transferId === t.id && expandedTransfer.reservation && (() => {
                          const prev = expandedTransfer.reservation;
                          const prevPay = expandedTransfer.payments || [];
                          const prevDocs = expandedTransfer.documents || [];
                          const prevTotal = prevPay.reduce((s, p) => s + Number(p.amount || 0), 0);
                          return (
                            <div style={{ marginTop: 8, background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 10, padding: 16 }}>
                              {/* التفاصيل المالية */}
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b", marginBottom: 10 }}>💰 التفاصيل المالية — حجز #{prev.id}</div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                                <div style={{ background: "#fff", padding: 10, borderRadius: 8, textAlign: "center", border: "1px solid #e2e8f0" }}>
                                  <div style={{ fontSize: 10, color: "#64748b" }}>سعر البيع</div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{fmt(prev.value)} ر.س</div>
                                </div>
                                <div style={{ background: "#fff", padding: 10, borderRadius: 8, textAlign: "center", border: "1px solid #e2e8f0" }}>
                                  <div style={{ fontSize: 10, color: "#64748b" }}>العربون</div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{fmt(prev.deposit_amount)} ر.س</div>
                                </div>
                                <div style={{ background: "#fff", padding: 10, borderRadius: 8, textAlign: "center", border: "1px solid #e2e8f0" }}>
                                  <div style={{ fontSize: 10, color: "#64748b" }}>الدلالة</div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{fmt(prev.dalala_amount)} ر.س</div>
                                </div>
                              </div>

                              {/* الدفعات */}
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>💳 الدفعات المسددة ({prevPay.length}) — إجمالي: {fmt(prevTotal)} ر.س</div>
                              {prevPay.length > 0 ? (
                                <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 14 }}>
                                  {prevPay.map((pay, pi) => (
                                    <div key={pi} style={{ padding: "8px 12px", background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 4 }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: 12, color: "#64748b" }}>{pay.payment_date?.slice(0, 10)}</span>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>{fmt(pay.amount)} ر.س</span>
                                      </div>
                                      {pay.notes && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{pay.notes}</div>}
                                      {pay.receipt_drive_link && (
                                        <a href={pay.receipt_drive_link} target="_blank" rel="noopener noreferrer"
                                          style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 11, color: "#2563eb", textDecoration: "none", background: "#eff6ff", padding: "2px 8px", borderRadius: 6 }}>
                                          📎 {pay.receipt_file_name || "سند القبض"}
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 14 }}>لا توجد دفعات</div>}

                              {/* المستندات */}
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>📄 المستندات ({prevDocs.length})</div>
                              {prevDocs.length > 0 ? (
                                <div style={{ maxHeight: 150, overflowY: "auto" }}>
                                  {prevDocs.map((doc, di) => (
                                    <div key={di} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "#fff", borderRadius: 6, border: "1px solid #e2e8f0", marginBottom: 4 }}>
                                      <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{doc.file_name}</div>
                                        <div style={{ fontSize: 10, color: "#94a3b8" }}>{doc.doc_type} — {doc.uploaded_at?.slice(0, 10)}</div>
                                      </div>
                                      {doc.drive_web_link && (
                                        <a href={doc.drive_web_link} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontSize: 12 }}>📥 تحميل</a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : <div style={{ fontSize: 11, color: "#94a3b8" }}>لا توجد مستندات</div>}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* تحديد الحجز الجديد — إذا لم يُحدد */}
                    {canManage && isPending && !t.to_reservation_id && (
                      <div style={{ background: "#eff6ff", padding: 12, borderRadius: 8, marginBottom: 12, border: "1px solid #bfdbfe" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", marginBottom: 6 }}>📋 تحديد الحجز الجديد</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input type="number" placeholder="رقم الحجز الجديد" id={`new-res-${t.id}`}
                            style={{ flex: 1, padding: "6px 10px", border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
                          <button onClick={async () => {
                            const val = document.getElementById(`new-res-${t.id}`).value;
                            if (!val) return toast.error("أدخل رقم الحجز");
                            try { await projectsApi.updateUnitTransfer(unitId, t.id, { to_reservation_id: parseInt(val) }); toast.success("تم تحديد الحجز"); loadData(); }
                            catch (e) { toast.error(e.response?.data?.error || "فشل"); }
                          }} style={{ padding: "6px 14px", background: "#1d4ed8", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            حفظ
                          </button>
                        </div>
                      </div>
                    )}

                    {/* المستندات */}
                    <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10 }}>📄 المستندات المطلوبة</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {/* اتفاقية التنازل */}
                        <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${t.assignment_doc_url ? "#bbf7d0" : "#fecaca"}`, background: t.assignment_doc_url ? "#f0fdf4" : "#fef2f2" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: t.assignment_doc_url ? "#166534" : "#991b1b", marginBottom: 6 }}>
                            {t.assignment_doc_url ? "✅" : "❌"} اتفاقية التنازل
                          </div>
                          {t.assignment_doc_url ? (
                            <a href={t.assignment_doc_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline" }}>
                              📎 {t.assignment_doc_name || "عرض المستند"}
                            </a>
                          ) : canManage && isPending ? (
                            <div>
                              <input type="text" placeholder="رابط Google Drive" id={`assign-url-${t.id}`}
                                style={{ width: "100%", padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11, marginBottom: 4, boxSizing: "border-box" }} />
                              <button onClick={async () => {
                                const url = document.getElementById(`assign-url-${t.id}`).value;
                                if (!url) return toast.error("أدخل رابط المستند");
                                try {
                                  await projectsApi.updateUnitTransfer(unitId, t.id, { assignment_doc_drive_id: "manual", assignment_doc_url: url, assignment_doc_name: "اتفاقية التنازل" });
                                  toast.success("تم رفع اتفاقية التنازل"); loadData();
                                } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
                              }} style={{ width: "100%", padding: "4px", background: "#166534", border: "none", borderRadius: 4, color: "#fff", fontSize: 11, cursor: "pointer" }}>
                                📎 إرفاق
                              </button>
                            </div>
                          ) : <span style={{ fontSize: 11, color: "#94a3b8" }}>غير مرفقة</span>}
                        </div>

                        {/* اتفاقية المخالصة */}
                        <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${t.clearance_doc_url ? "#bbf7d0" : "#fecaca"}`, background: t.clearance_doc_url ? "#f0fdf4" : "#fef2f2" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: t.clearance_doc_url ? "#166534" : "#991b1b", marginBottom: 6 }}>
                            {t.clearance_doc_url ? "✅" : "❌"} اتفاقية المخالصة
                          </div>
                          {t.clearance_doc_url ? (
                            <a href={t.clearance_doc_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline" }}>
                              📎 {t.clearance_doc_name || "عرض المستند"}
                            </a>
                          ) : canManage && isPending ? (
                            <div>
                              <input type="text" placeholder="رابط Google Drive" id={`clear-url-${t.id}`}
                                style={{ width: "100%", padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 11, marginBottom: 4, boxSizing: "border-box" }} />
                              <button onClick={async () => {
                                const url = document.getElementById(`clear-url-${t.id}`).value;
                                if (!url) return toast.error("أدخل رابط المستند");
                                try {
                                  await projectsApi.updateUnitTransfer(unitId, t.id, { clearance_doc_drive_id: "manual", clearance_doc_url: url, clearance_doc_name: "اتفاقية المخالصة" });
                                  toast.success("تم رفع اتفاقية المخالصة"); loadData();
                                } catch (e) { toast.error(e.response?.data?.error || "فشل"); }
                              }} style={{ width: "100%", padding: "4px", background: "#166534", border: "none", borderRadius: 4, color: "#fff", fontSize: 11, cursor: "pointer" }}>
                                📎 إرفاق
                              </button>
                            </div>
                          ) : <span style={{ fontSize: 11, color: "#94a3b8" }}>غير مرفقة</span>}
                        </div>
                      </div>
                    </div>

                    {t.notes && <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10, background: "#f8fafc", padding: 8, borderRadius: 6 }}>📝 {t.notes}</div>}

                    {/* أزرار التحكم */}
                    {canManage && isPending && (
                      <div style={{ display: "flex", gap: 8, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
                        {t.status === "ready" && (
                          <button onClick={async () => {
                            if (!window.confirm("هل أنت متأكد من تنفيذ النقل؟\n\n• سيتم إقفال الحجز القديم\n• ربط الوحدة بالحجز الجديد\n\nهذا الإجراء لا يمكن التراجع عنه.")) return;
                            try { await projectsApi.executeUnitTransfer(unitId, t.id); toast.success("✅ تم تنفيذ النقل بنجاح"); loadData(); }
                            catch (e) { toast.error(e.response?.data?.error || "فشل تنفيذ النقل"); }
                          }} style={{ flex: 1, padding: "10px", background: "#166534", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                            ✅ تنفيذ النقل
                          </button>
                        )}
                        {t.status === "pending_docs" && (
                          <div style={{ flex: 1, padding: "10px", background: "#fef9c3", borderRadius: 8, textAlign: "center", fontSize: 12, color: "#854d0e", fontWeight: 600 }}>
                            ⏳ أرفق المستندين وحدد الحجز الجديد للمتابعة
                          </div>
                        )}
                        <button onClick={async () => {
                          if (!window.confirm("هل تريد إلغاء طلب النقل؟")) return;
                          try { await projectsApi.cancelUnitTransfer(unitId, t.id); toast.success("تم إلغاء الطلب"); loadData(); }
                          catch (e) { toast.error(e.response?.data?.error || "فشل"); }
                        }} style={{ padding: "10px 16px", background: "#fff", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          إلغاء الطلب
                        </button>
                      </div>
                    )}

                    {t.status === "completed" && t.executed_at && (
                      <div style={{ fontSize: 11, color: "#166534", marginTop: 8 }}>✅ تم التنفيذ بتاريخ {t.executed_at?.slice(0, 10)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ═══ نافذة بدء طلب النقل ═══ */}
      {showTransferModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowTransferModal(false)}>
          <div dir="rtl" onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: 24, width: 480, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 25px 50px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🔄 نقل / تنازل عن الوحدة</h3>
              <button onClick={() => setShowTransferModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>×</button>
            </div>

            <div style={{ background: "#f8fafc", borderRadius: 10, padding: 14, marginBottom: 16, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>الوحدة الحالية</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{unit.unit_label || unit.unit_code}</div>
              {r && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>الحجز الحالي: #{r.id} — {r.client_name}</div>}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>نوع العملية</label>
              <select value={transferForm.transfer_type} onChange={e => setTransferForm(f => ({...f, transfer_type: e.target.value}))}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }}>
                <option value="تنازل">تنازل عن العقد</option>
                <option value="إعادة بيع">إعادة بيع</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>رقم الحجز الجديد (اختياري الآن — يمكن إضافته لاحقاً)</label>
              <input type="number" value={transferForm.to_reservation_id} onChange={e => setTransferForm(f => ({...f, to_reservation_id: e.target.value}))}
                placeholder="أدخل رقم الحجز الجديد"
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>ملاحظات</label>
              <textarea value={transferForm.notes} onChange={e => setTransferForm(f => ({...f, notes: e.target.value}))}
                rows={3} placeholder="سبب النقل أو ملاحظات إضافية..."
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
            </div>

            <div style={{ background: "#fffbeb", borderRadius: 8, padding: 12, marginBottom: 16, border: "1px solid #fef08a" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>⚠️ المستندات المطلوبة قبل التنفيذ:</div>
              <div style={{ fontSize: 11, color: "#78350f" }}>1. اتفاقية التنازل عن العقد (موقعة من الطرفين)</div>
              <div style={{ fontSize: 11, color: "#78350f" }}>2. اتفاقية المخالصة (لإقفال العقد والحجز القديم)</div>
              <div style={{ fontSize: 11, color: "#78350f", marginTop: 4 }}>يمكن رفع المستندات بعد إنشاء الطلب من قسم "تاريخ نقل الوحدة"</div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={async () => {
                try {
                  await projectsApi.createUnitTransfer(unitId, {
                    to_reservation_id: transferForm.to_reservation_id ? parseInt(transferForm.to_reservation_id) : null,
                    transfer_type: transferForm.transfer_type,
                    notes: transferForm.notes
                  });
                  toast.success("✅ تم إنشاء طلب النقل — قم برفع المستندات المطلوبة");
                  setShowTransferModal(false);
                  setTransferForm({ to_reservation_id: "", transfer_type: "تنازل", notes: "" });
                  loadData();
                } catch (e) { toast.error(e.response?.data?.error || "فشل إنشاء طلب النقل"); }
              }} style={{ flex: 1, padding: "10px", background: "#166534", border: "none", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                إنشاء طلب النقل
              </button>
              <button onClick={() => setShowTransferModal(false)}
                style={{ padding: "10px 20px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
